/**
 * API Normalizer Utility
 * 
 * This utility ensures consistent camelCase property names when working with API data.
 * The C# API uses PascalCase internally but serializes to camelCase over the wire.
 * This normalizer handles any edge cases where data might arrive in PascalCase.
 * 
 * Usage:
 * - Import normalizeVariable, normalizeNotification, etc. when processing API responses
 * - Use these functions when creating objects from events or user input
 * - This prevents casing mismatches from causing bugs (e.g., empty dropdowns, failed validation)
 * 
 * Why this exists:
 * - ASP.NET Core serializes C# PascalCase to JSON camelCase by default
 * - But internal UI components (like TemplateStringInput) may dispatch PascalCase
 * - This normalizer provides a single source of truth for property names
 */

import type { WorkflowTemplateVariable } from "../types/WorkflowTemplates/WorkflowTemplateVariable";
import { effectiveSftpAuthType, normalizeSftpAuthType, SFTP_AUTH_TYPE } from "../constants/sftp-auth-type";
import { isSftpSshKeyAuthFeatureEnabled } from "../utils/sftp-ssh-key-feature-gate";
import {
  flattenFileInfoFromStep,
  stepNeedsNestedFileInfoFallback,
} from "./workflow-file-info-leg";

/**
 * Normalize a variable object to ensure camelCase property names.
 * Handles both camelCase and PascalCase input.
 */
export function normalizeVariable(input: Record<string, any>): WorkflowTemplateVariable {
  return {
    name: input.name ?? input.Name ?? "",
    description: input.description ?? input.Description ?? "",
    regexRule: input.regexRule ?? input.RegexRule ?? "",
    ruleDescription: input.ruleDescription ?? input.RuleDescription ?? "",
    isRequired: input.isRequired ?? input.IsRequired ?? false,
    // Preserve UI-only fields if they exist (already camelCase)
    variableId: input.variableId,
    isEditing: input.isEditing,
    originalValues: input.originalValues,
    // API-only fields
    createdBy: input.createdBy ?? input.CreatedBy,
    createdDateTime: input.createdDateTime ?? input.CreatedDateTime,
    eTag: input.eTag ?? input.ETag,
  };
}

/**
 * Normalize an array of variables
 */
export function normalizeVariables(inputs: Record<string, any>[]): WorkflowTemplateVariable[] {
  return (inputs || []).map(normalizeVariable);
}

/**
 * Notification structure matching API
 */
export interface NormalizedNotification {
  to: string;
  subject: string;
  notificationType: string;
  template: string;
  messageType: string;
}

/**
 * Normalize a notification object to ensure camelCase property names.
 */
export function normalizeNotification(input: Record<string, any>): NormalizedNotification {
  return {
    to: input.to ?? input.To ?? "",
    subject: input.subject ?? input.Subject ?? "",
    notificationType: input.notificationType ?? input.NotificationType ?? "",
    template: input.template ?? input.Template ?? "",
    messageType: input.messageType ?? input.MessageType ?? "",
  };
}

/**
 * Normalize an array of notifications
 */
export function normalizeNotifications(inputs: Record<string, any>[]): NormalizedNotification[] {
  return (inputs || []).map(normalizeNotification);
}

/**
 * Scheduler structure matching API
 */
export interface NormalizedScheduler {
  schedulerName: string;
  description: string;
  expression: string;
  timezone: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

/**
 * Normalize a scheduler object to ensure camelCase property names.
 */
export function normalizeScheduler(input: Record<string, any>): NormalizedScheduler {
  return {
    schedulerName: input.schedulerName ?? input.SchedulerName ?? "",
    description: input.description ?? input.Description ?? "",
    expression: input.expression ?? input.Expression ?? "",
    timezone: input.timezone ?? input.Timezone ?? input.TimeZone ?? "",
    startDate: input.startDate ?? input.StartDate ?? "",
    startTime: input.startTime ?? input.StartTime ?? "",
    endDate: input.endDate ?? input.EndDate ?? "",
    endTime: input.endTime ?? input.EndTime ?? "",
  };
}

// ============================================
// Workflow Step Normalizer
// ============================================

/**
 * Workflow step structure matching API
 * Note: AddWorkflowStepPopup uses PascalCase for some fields (HostName, Port, etc.)
 * but the API returns camelCase. This normalizer ensures consistent camelCase.
 */
export interface NormalizedStep {
  stepIndex: number;
  stepName: string;
  functionName: string;
  deleteSourceFile: boolean | string;
  fileMovePattern: string[];
  locationType: string;
  storageType: string;
  name: string;
  path: string;
  bucketRename: string;
  fileRename: Record<string, string>;
  secretName: string;
  secretValue: Record<string, string>;
  hostName: string;
  port: string;
  userName: string;
  password: string;
  pgpKey: string;
  connectionString: string;
  roleARN: string;
  region: string;
  /** FileLocation.authType (DynamoDB): e.g. password | ssh; not stored in Secrets Manager. */
  authType: string;
  /** Per-leg SFTP auth for SourceFileInfo (Password | SshKey) when persisted separately from destination. */
  sourceAuthType: string;
  /** Per-leg SFTP auth for DestinationFileInfo (Password | SshKey) when persisted separately from source. */
  destinationAuthType: string;
  isValidStep?: boolean;
  isUnprocessArchive?: boolean;
}

/**
 * Normalize a workflow step object to ensure camelCase property names.
 * Handles both camelCase (from API) and PascalCase (from AddWorkflowStepPopup).
 * 
 * This is critical because:
 * - API returns camelCase: hostName, port, userName, fileMovePattern
 * - AddWorkflowStepPopup returns PascalCase: HostName, Port, UserName, FileMovePattern
 * - Without normalization, editing API-loaded steps shows empty fields
 */
export function normalizeStep(input: Record<string, any>): NormalizedStep {
  // Normalize secretValue internal fields if present
  let normalizedSecretValue: Record<string, string> = {};
  const sv = input.secretValue ?? input.SecretValue ?? {};
  if (sv && typeof sv === "object") {
    normalizedSecretValue = {
      host: sv.host ?? sv.Host ?? "",
      port: sv.port ?? sv.Port ?? "",
      userName: sv.userName ?? sv.UserName ?? "",
      password: sv.password ?? sv.Password ?? "",
      pgpKey: sv.pgpKey ?? sv.PGPKey ?? "",
      connectionString: sv.connectionString ?? sv.ConnectionString ?? "",
      roleARN: sv.roleARN ?? sv.RoleARN ?? "",
      region: sv.region ?? sv.Region ?? "",
      privateKey: isSftpSshKeyAuthFeatureEnabled()
        ? sv.privateKey ?? sv.sshPrivateKeyReference ?? sv.SshPrivateKeyReference ?? ""
        : "",
    };
  }

  const authTypeRaw =
    input.authType ?? input.AuthType ?? sv.authType ?? sv.AuthType ?? "";
  let authTypeOut = authTypeRaw;
  if (!isSftpSshKeyAuthFeatureEnabled() && normalizeSftpAuthType(authTypeOut) === SFTP_AUTH_TYPE.SshKey) {
    authTypeOut = "password";
    normalizedSecretValue.privateKey = "";
  }

  const sourceAuthTypeOut = effectiveSftpAuthType(
    (input.sourceAuthType ?? input.SourceAuthType ?? authTypeOut) as string
  );
  const destinationAuthTypeOut = effectiveSftpAuthType(
    (input.destinationAuthType ?? input.DestinationAuthType ?? authTypeOut) as string
  );

  // Popup may send fresh top-level credential fields from template-string-input while secretValue lags (masked-input path only syncs secretValue).
  const topConnectionString = input.ConnectionString || input.connectionString || "";
  if (topConnectionString) {
    normalizedSecretValue.connectionString = topConnectionString;
  }
  const topPgpKey = input.PGPKey || input.pgpKey || "";
  if (topPgpKey) {
    normalizedSecretValue.pgpKey = topPgpKey;
  }
  // Avoid `||` so numeric 0 (legacy bug from FocusEvent + mixin) does not skip real string keys.
  const topPrivateKeyRaw =
    input.privateKey ?? input.SshPrivateKeyReference ?? input.sshPrivateKeyReference ?? "";
  const topPrivateKey =
    topPrivateKeyRaw === 0 || topPrivateKeyRaw === false
      ? ""
      : typeof topPrivateKeyRaw === "string"
        ? topPrivateKeyRaw
        : topPrivateKeyRaw != null
          ? String(topPrivateKeyRaw)
          : "";
  if (topPrivateKey && isSftpSshKeyAuthFeatureEnabled()) {
    normalizedSecretValue.privateKey = topPrivateKey;
  }

  // For FileMovePattern, prefer PascalCase (popup's output) if it has items,
  // since the popup updates FileMovePattern on save but the spread may have old fileMovePattern
  const camelFMP = input.fileMovePattern ?? [];
  const pascalFMP = input.FileMovePattern ?? [];
  const fileMovePatternResult = pascalFMP.length > 0 ? pascalFMP : camelFMP;

  // For RoleARN/Region: check multiple sources in priority order
  // 1. Direct PascalCase on input (from popup output)
  // 2. Direct camelCase on input
  // 3. From secretValue (PascalCase)
  // 4. From secretValue (camelCase)
  const roleARN = input.RoleARN || input.roleARN ||
    normalizedSecretValue.roleARN ||
    sv?.RoleARN || sv?.roleARN || "";
  const region = input.Region || input.region ||
    normalizedSecretValue.region ||
    sv?.Region || sv?.region || "";

  // Keep nested secretValue in sync with resolved top-level credentials (same as ConnectionString/PGPKey overlay).
  normalizedSecretValue.roleARN = roleARN;
  normalizedSecretValue.region = region;

  return {
    stepIndex: input.stepIndex ?? input.StepIndex ?? 0,
    stepName: input.stepName ?? input.StepName ?? "",
    functionName: input.functionName ?? input.FunctionName ?? "",
    deleteSourceFile: input.deleteSourceFile ?? input.DeleteSourceFile ?? false,
    fileMovePattern: fileMovePatternResult,
    locationType: input.locationType ?? input.LocationType ?? "",
    storageType: input.storageType ?? input.StorageType ?? "",
    name: input.name ?? input.Name ?? "",
    path: input.path ?? input.Path ?? "",
    bucketRename: input.bucketRename ?? input.BucketRename ?? "",
    fileRename: input.fileRename ?? input.FileRename ?? {},
    secretName: input.secretName ?? input.SecretName ?? "",
    secretValue: normalizedSecretValue,
    // For credential fields, prefer PascalCase (popup's output) if it has a value,
    // since the popup updates PascalCase fields but the spread may have empty camelCase
    hostName: input.HostName || input.hostName || "",
    port: input.Port || input.port || "",
    userName: input.UserName || input.userName || "",
    password: input.Password || input.password || "",
    pgpKey: input.PGPKey || input.pgpKey || "",
    connectionString: input.ConnectionString || input.connectionString || "",
    roleARN: roleARN,
    region: region,
    authType: authTypeOut,
    sourceAuthType: sourceAuthTypeOut,
    destinationAuthType: destinationAuthTypeOut,
    isValidStep: input.isValidStep ?? input.IsValidStep,
    isUnprocessArchive: input.isUnprocessArchive ?? input.IsUnprocessArchive,
  };
}

/**
 * Normalize an array of workflow steps
 */
export function normalizeSteps(inputs: Record<string, any>[]): NormalizedStep[] {
  return (inputs || []).map(normalizeStep);
}

/**
 * Convert a normalized step (camelCase) to popup format (PascalCase).
 * Use this when passing data TO AddWorkflowStepPopup for editing.
 * 
 * The popup expects PascalCase for most SFTP/FTPS/Azure/AWS fields because
 * that's how its formData is defined internally (SSH key reference uses privateKey).
 */
export function stepToPopupFormat(
  step: Record<string, any>,
  transferType?: string
): Record<string, any> {
  let resolvedStep = step;

  if (transferType && stepNeedsNestedFileInfoFallback(step)) {
    const flat = flattenFileInfoFromStep(step, transferType);
    resolvedStep = {
      ...step,
      locationType: flat.locationType,
      storageType: flat.storageType,
      name: flat.name,
      path: flat.path,
      fileRename: flat.fileRename,
      bucketRename: flat.bucketRename,
      secretName: flat.secretName,
      secretValue: flat.secretValue,
      hostName: flat.hostName,
      port: flat.port,
      userName: flat.userName,
      password: flat.password,
      pgpKey: flat.pgpKey,
      connectionString: flat.connectionString,
      roleARN: flat.roleARN,
      region: flat.region,
    };
  }

  // Convert secretValue to keys the popup expects (mixed PascalCase + privateKey)
  const sv = resolvedStep.secretValue ?? {};

  // For RoleARN/Region in secretValue: check all possible sources
  const svRoleARN = sv?.RoleARN ?? sv?.roleARN ?? "";
  const svRegion = sv?.Region ?? sv?.region ?? "";

  const secretValueForPopup =
    sv && typeof sv === "object"
      ? {
        Host: sv.host ?? sv.Host ?? "",
        Port: sv.port ?? sv.Port ?? "",
        UserName: sv.userName ?? sv.UserName ?? "",
        Password: sv.password ?? sv.Password ?? "",
        PGPKey: sv.pgpKey ?? sv.PGPKey ?? "",
        ConnectionString: sv.connectionString ?? sv.ConnectionString ?? "",
        RoleARN: svRoleARN,
        Region: svRegion,
        privateKey: isSftpSshKeyAuthFeatureEnabled()
          ? sv.privateKey ?? sv.sshPrivateKeyReference ?? sv.SshPrivateKeyReference ?? ""
          : "",
      }
      : {};

  // For top-level RoleARN/Region: check step properties AND secretValue
  // Priority: step.RoleARN > step.roleARN > secretValue.RoleARN
  const topLevelRoleARN = resolvedStep.RoleARN || resolvedStep.roleARN || svRoleARN || "";
  const topLevelRegion = resolvedStep.Region || resolvedStep.region || svRegion || "";

  return {
    ...resolvedStep,
    // Preserve camelCase fields
    stepIndex: resolvedStep.stepIndex,
    stepName: resolvedStep.stepName,
    functionName: resolvedStep.functionName,
    deleteSourceFile: resolvedStep.deleteSourceFile,
    locationType: resolvedStep.locationType,
    storageType: resolvedStep.storageType,
    name: resolvedStep.name,
    path: resolvedStep.path,
    bucketRename: resolvedStep.bucketRename,
    fileRename: resolvedStep.fileRename,
    secretName: resolvedStep.secretName,
    // Convert secretValue to PascalCase keys
    secretValue: secretValueForPopup,
    // Add PascalCase versions that the popup expects
    HostName: resolvedStep.hostName ?? resolvedStep.HostName ?? "",
    Port: resolvedStep.port ?? resolvedStep.Port ?? "",
    UserName: resolvedStep.userName ?? resolvedStep.UserName ?? "",
    Password: resolvedStep.password ?? resolvedStep.Password ?? "",
    PGPKey: resolvedStep.pgpKey ?? resolvedStep.PGPKey ?? "",
    ConnectionString: resolvedStep.connectionString ?? resolvedStep.ConnectionString ?? "",
    RoleARN: topLevelRoleARN,
    Region: topLevelRegion,
    AuthType: effectiveSftpAuthType(
      (resolvedStep.authType ?? resolvedStep.AuthType ?? sv?.authType ?? sv?.AuthType ?? "") as string
    ),
    sourceAuthType: effectiveSftpAuthType(
      (resolvedStep.sourceAuthType ??
        resolvedStep.SourceAuthType ??
        resolvedStep.authType ??
        resolvedStep.AuthType ??
        "") as string
    ),
    destinationAuthType: effectiveSftpAuthType(
      (resolvedStep.destinationAuthType ??
        resolvedStep.DestinationAuthType ??
        resolvedStep.authType ??
        resolvedStep.AuthType ??
        "") as string
    ),
    privateKey: isSftpSshKeyAuthFeatureEnabled()
      ? resolvedStep.privateKey ??
      resolvedStep.sshPrivateKeyReference ??
      resolvedStep.SshPrivateKeyReference ??
      sv?.privateKey ??
      sv?.sshPrivateKeyReference ??
      sv?.SshPrivateKeyReference ??
      ""
      : "",
    FileMovePattern: resolvedStep.fileMovePattern ?? resolvedStep.FileMovePattern ?? [],
  };
}

// ============================================
// Generic Helpers
// ============================================

/**
 * Generic helper to convert a single property name from PascalCase to camelCase
 */
export function toCamelCase(str: string): string {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

/**
 * Generic helper to normalize all keys in an object to camelCase
 * Useful for unknown/dynamic objects from API responses
 */
export function normalizeKeys<T extends Record<string, any>>(obj: T): Record<string, any> {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(normalizeKeys) as any;

  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const camelKey = toCamelCase(key);
    const value = obj[key];
    result[camelKey] = typeof value === "object" && value !== null
      ? normalizeKeys(value)
      : value;
  }
  return result;
}
